import React from 'react';
import { Box, Heading, Icon, Text, Tooltip } from '@zillow/constellation-10';
import type { AdvancedTableFilterOption, ColumnConfig } from '@zg-rentals/advanced-table';
import { AdvancedTable } from '@zg-rentals/advanced-table';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { numberUtils, useIsScreenDesktop } from '@zg-rentals/rental-manager-env';
import billingUtils from '../../utils/billingUtils';
import { calculateFilters } from './calculateFilters';
import { StyledContainer } from './StyledComponents';
import type { InvoiceItemFilters } from '../../model/InvoiceItemFilters';
import { emitSetFilterEvent, EVENT_IDS } from '../../model/ClickstreamEvents';
import {
  type AdproductsZuoraBillingInvoiceDto,
  type AdproductsZuoraBillingInvoiceItemDto,
  ZuoraInvoiceType,
} from '@zg-rentals/ts-adproducts-client';
import { IconCalendarOutline, IconQuestionMarkCircleFilled } from '@zillow/constellation-icons';

const mapActiveRanges = (activeRanges: string) => {
  if (!activeRanges) {
    return '-';
  }
  return activeRanges.split(';').map((range) => (
    <div key={range} className="InvoicePage-active-range">
      {range}
    </div>
  ));
};

export interface SfhBreakdownTableProps {
  currentInvoice: AdproductsZuoraBillingInvoiceDto;
}

export const SfhBreakdownTable = ({ currentInvoice }: SfhBreakdownTableProps) => {
  const isScreenDesktop = useIsScreenDesktop();
  const { user } = useRentalManagerUser();
  const [currentInvoiceListingBreakdowns, setCurrentInvoiceListingBreakdowns] = React.useState<
    Array<AdproductsZuoraBillingInvoiceItemDto>
  >([]);

  const isFlatFeeFeedConnect = billingUtils.isFlatFeeFeedConnectInvoice(currentInvoiceListingBreakdowns);
  const isSingleFamilyLegacy =
    currentInvoice &&
    (currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_REIT ||
      currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_FLAT_FEE);

  const amountHeaderRenderer = () => (isSingleFamilyLegacy || isFlatFeeFeedConnect ? 'EST. AMOUNT' : 'AMOUNT');
  const chargeItemTooltipMessage = isFlatFeeFeedConnect
    ? 'Billed a flat rate per month at the account level regardless of number of listings posted.'
    : 'Billed per published unit per day.';
  const [listingBreakdownFilters, setListingBreakdownFilters] = React.useState<InvoiceItemFilters>({});

  React.useEffect(() => {
    if (currentInvoice) {
      const newFilters = calculateFilters(currentInvoice);
      setListingBreakdownFilters(newFilters);
    }
  }, [currentInvoice]);

  let invoiceColumnConfig: Array<ColumnConfig<AdproductsZuoraBillingInvoiceItemDto>> = [
    {
      width: 4,
      header: 'Property',
      dataKey: 'propertyName',
      align: 'left',
    },
    {
      width: 2,
      align: 'left',
      header: 'Charge Item',
      dataKey: 'chargeName',
      renderer: (data: AdproductsZuoraBillingInvoiceItemDto) => {
        if (data.chargeName.includes('Zillow Feed Connect')) {
          return (
            <Tooltip
              useHoverProps={{
                delay: 100,
                restMs: 100,
              }}
              content={chargeItemTooltipMessage}
              trigger={
                <Text textStyle="body-xs" css={{ display: 'flex', alignItems: 'center' }}>
                  {data.chargeName}
                  <Icon size="sm">
                    <IconQuestionMarkCircleFilled />
                  </Icon>
                </Text>
              }
            />
          );
        }
        return data.chargeName;
      },
    },
    {
      width: 1,
      header:
        currentInvoice &&
        (currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_DAILY_LISTINGS ||
          currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_REIT ||
          currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_FLAT_FEE)
          ? 'Active Days'
          : 'QTY',
      dataKey: 'quantity',
      align: 'right',
      renderer: (data: AdproductsZuoraBillingInvoiceItemDto) => {
        const { quantity, activeRanges } = data;
        if (isNaN(quantity)) return '-';
        if (data.chargeName.includes('Zillow Feed Connect')) {
          return (
            <Tooltip
              useHoverProps={{
                delay: 100,
                restMs: 100,
              }}
              trigger={
                <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  {quantity % 1 === 0 ? quantity : quantity.toFixed(2)}
                  <Icon size="md">
                    <IconCalendarOutline />
                  </Icon>
                </Box>
              }
            >
              {mapActiveRanges(activeRanges)}
            </Tooltip>
          );
        }
        return quantity % 1 === 0 ? quantity : quantity.toFixed(2);
      },
    },
    {
      width: 2,
      header:
        currentInvoice && currentInvoice.invoiceType === ZuoraInvoiceType.SINGLE_FAMILY_DAILY_LISTINGS
          ? 'Daily Rate'
          : 'Unit Price',
      customKey: 'unitPrice',
      renderer: (value: AdproductsZuoraBillingInvoiceItemDto) => {
        if (!value || !value.quantity) return '-';
        const formattedDiscount = billingUtils.formatDiscountRate(value);
        const formattedValue = numberUtils.asCurrencyWithDefault(value.chargeAmount / value.quantity, '-');
        return (
          <span>
            {formattedValue}
            {formattedDiscount && <div>{formattedDiscount}</div>}
          </span>
        );
      },
      align: 'right',
    },
    {
      width: 2,
      dataKey: 'chargeAmount',
      headerRenderer: amountHeaderRenderer,
      renderer: (value: AdproductsZuoraBillingInvoiceItemDto) => {
        const formattedValue = numberUtils.asCurrency(value.chargeAmount);
        const formattedDiscount = billingUtils.formatDiscountAmount(value);
        return (
          <span>
            {formattedValue}
            {formattedDiscount && <div>{formattedDiscount}</div>}
          </span>
        );
      },
      align: 'right',
    },
  ];

  // Hide unit price if single family legacy or flat fee feed connect
  if (isSingleFamilyLegacy || isFlatFeeFeedConnect) {
    invoiceColumnConfig = invoiceColumnConfig.filter((col) => col.customKey !== 'unitPrice');
  }

  const mobileColumnConfig: Array<ColumnConfig<AdproductsZuoraBillingInvoiceItemDto>> = [
    {
      width: 4,
      header: 'Property',
      dataKey: 'propertyName',
      align: 'left',
    },
    {
      width: 4,
      header: 'Product',
      dataKey: 'chargeName',
      align: 'left',
    },
    {
      width: 3,
      dataKey: 'chargeAmount',
      headerRenderer: amountHeaderRenderer,
      renderer: (value: AdproductsZuoraBillingInvoiceItemDto) => numberUtils.asCurrency(value.chargeAmount),
      align: 'right',
    },
  ];

  const columnConfig = isScreenDesktop ? invoiceColumnConfig : mobileColumnConfig;

  // Handle filter breakdowns
  React.useEffect(() => {
    if (!currentInvoice) return setCurrentInvoiceListingBreakdowns([]);
    if (!Object.keys(listingBreakdownFilters).length)
      return setCurrentInvoiceListingBreakdowns(currentInvoice.sfhLineItems);
    let filteredItems = currentInvoice.sfhLineItems.slice();
    if (listingBreakdownFilters.chargeName) {
      const filterSet = new Set();
      listingBreakdownFilters.chargeName.value.forEach((v: AdvancedTableFilterOption) => {
        if (v.isChecked) filterSet.add(v.label);
      });
      if (filterSet.size) {
        filteredItems = filteredItems.filter((item) => filterSet.has(item.chargeName));
      }
    }
    if (listingBreakdownFilters.city) {
      const filterSet = new Set();
      listingBreakdownFilters.city.value.forEach((v: AdvancedTableFilterOption) => {
        if (v.isChecked) filterSet.add(v.label);
      });
      if (filterSet.size) {
        filteredItems = filteredItems.filter((item) => filterSet.has(item.city));
      }
    }
    if (listingBreakdownFilters.propertyName) {
      const filterSet = new Set();
      listingBreakdownFilters.propertyName.value.forEach((v: AdvancedTableFilterOption) => {
        if (v.isChecked) filterSet.add(v.label);
      });
      if (filterSet.size) {
        filteredItems = filteredItems.filter((item) => filterSet.has(item.propertyName));
      }
    }
    if (listingBreakdownFilters.state) {
      const filterSet = new Set();
      listingBreakdownFilters.state.value.forEach((v: AdvancedTableFilterOption) => {
        if (v.isChecked) filterSet.add(v.label);
      });
      if (filterSet.size) {
        filteredItems = filteredItems.filter((item) => filterSet.has(item.state));
      }
    }

    setCurrentInvoiceListingBreakdowns(filteredItems);
  }, [listingBreakdownFilters, currentInvoice]);

  return (
    <StyledContainer data-eventid={EVENT_IDS.SET_FILTER} data-eventdescription="Set filter event">
      <Heading level={4}>Listing breakdown</Heading>
      <Box css={{ margin: 'xs' }} />
      <AdvancedTable
        onSaveFilters={(newFilters: InvoiceItemFilters) => {
          emitSetFilterEvent({ user });
          setListingBreakdownFilters(newFilters);
        }}
        columnConfig={columnConfig}
        data={currentInvoiceListingBreakdowns}
        filters={listingBreakdownFilters}
        isMobile={!isScreenDesktop}
      />
      <Box css={{ margin: 'xs' }} />
    </StyledContainer>
  );
};
